/**
 * StaticPlannerPanel Component
 * 
 * Pre-race strategy optimizer UI. Runs the StaticStrategyPlanner with
 * the user's current inputs and displays all ranked race plans in a
 * comparison table with expandable stint breakdowns.
 */

import React, { useEffect, useMemo, useState } from "react";
import { StaticStrategyPlanner, StaticStrategyResult, StaticStrategyInput, RacePlan, PlanStint, PaceProfile } from "../utils/staticStrategyPlanner";
import type { CarClass } from "../utils/liveStrategyCalculator";
import type { FuelSavingCalibration } from "../types/strategyCalibration";
import { buildFuelSavingModel } from "../utils/fuelSavingCalibration";
import { TimePicker } from "./TimePicker";
import { FuelInput } from "./FuelInput";

interface StaticPlannerPanelProps {
  raceDurationMinutes: number;
  fuelCapacity: number;
  avgFuelPerLap: number;
  avgLapTime: number;
  pitInTime?: number;
  pitOutTime?: number;
  fuelingTime?: number;
  prefillFuelSavingCalibration?: FuelSavingCalibration;
  onSelectPlan?: (plan: RacePlan, input: StaticStrategyInput) => void;
  selectedPlanId?: string | null;
}

const CAR_CLASSES: { value: CarClass | 'none'; label: string }[] = [
  { value: 'GTP', label: 'GTP (LMDh)' },
  { value: 'LMP2', label: 'LMP2' },
  { value: 'GT3', label: 'GT3' },
  { value: 'none', label: 'Custom / Unknown' },
];

const CATEGORY_COLORS: Record<string, string> = {
  full_speed:      'bg-green-500/20 text-green-400 border-green-500/40',
  fuel_save:       'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
  mixed_pit:       'bg-blue-500/20 text-blue-400 border-blue-500/40',
  mixed_pit_save:  'bg-purple-500/20 text-purple-400 border-purple-500/40',
  no_stop:         'bg-cyan-500/20 text-cyan-400 border-cyan-500/40',
};

const CATEGORY_LABELS: Record<string, string> = {
  full_speed:      'Full Speed',
  fuel_save:       'Fuel Save',
  mixed_pit:       'Mixed Pit',
  mixed_pit_save:  'Mixed+Save',
  no_stop:         'No Stop',
};

function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatLapTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = (seconds % 60).toFixed(2);
  return `${m}:${parseFloat(s) < 10 ? '0' : ''}${s}`;
}

function formatSavePercent(value: number): string {
  return `${value.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1')}%`;
}

interface PlanTradeoff {
  paceDelta: number;
  pitDelta: number;
  netDelta: number;
}

function getPlanTradeoff(plan: RacePlan, baseline?: RacePlan): PlanTradeoff | null {
  if (!baseline || plan.id === baseline.id || baseline.totalLaps !== plan.totalLaps) {
    return null;
  }

  return {
    paceDelta: plan.totalDrivingTime - baseline.totalDrivingTime,
    pitDelta: baseline.totalPitTime - plan.totalPitTime,
    netDelta: plan.totalRaceTime - baseline.totalRaceTime,
  };
}

/** Returns how many L/lap the plan's longest stint requires — and whether the
 *  user's measured fuel rate fits within that budget. */
function getStintFeasibility(
  plan: RacePlan,
  input: StaticStrategyInput,
): { maxFuelRate: number; ok: boolean; delta: number } | null {
  if (plan.maxStintLaps <= 0 || !input.fuelCapacity || !input.avgFuelPerLap) return null;
  const margin = Math.max(0.05, input.fuelSafetyMargin ?? 0.3);
  const maxFuelRate = (input.fuelCapacity - margin) / plan.maxStintLaps;
  const delta = input.avgFuelPerLap - maxFuelRate;
  return { maxFuelRate, ok: delta <= 0, delta };
}

const StrategyTradeoffCard: React.FC<{ tradeoff: PlanTradeoff; baselineName: string }> = ({ tradeoff, baselineName }) => (
  <div className="mt-4 rounded-lg border border-slate-700 bg-slate-950/40 p-3">
    <div className="flex items-center justify-between gap-4 mb-3">
      <div>
        <div className="text-sm font-semibold text-white">Why This Beats The Baseline</div>
        <div className="text-xs text-slate-400 mt-1">Compared with {baselineName}</div>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
      <div className="rounded border border-slate-700 bg-slate-900/40 p-3">
        <div className="text-slate-400 text-xs uppercase tracking-wide mb-1">Pace Loss From Saving</div>
        <div className={`font-semibold ${tradeoff.paceDelta > 0 ? 'text-red-400' : 'text-green-400'}`}>
          {tradeoff.paceDelta > 0 ? '+' : ''}{tradeoff.paceDelta.toFixed(1)}s
        </div>
      </div>
      <div className="rounded border border-slate-700 bg-slate-900/40 p-3">
        <div className="text-slate-400 text-xs uppercase tracking-wide mb-1">Pit Time Gained</div>
        <div className={`font-semibold ${tradeoff.pitDelta > 0 ? 'text-green-400' : 'text-red-400'}`}>
          {tradeoff.pitDelta > 0 ? '+' : ''}{tradeoff.pitDelta.toFixed(1)}s
        </div>
      </div>
      <div className="rounded border border-slate-700 bg-slate-900/40 p-3">
        <div className="text-slate-400 text-xs uppercase tracking-wide mb-1">Net Result</div>
        <div className={`font-semibold ${tradeoff.netDelta <= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {tradeoff.netDelta <= 0 ? `${Math.abs(tradeoff.netDelta).toFixed(1)}s faster` : `${tradeoff.netDelta.toFixed(1)}s slower`}
        </div>
      </div>
    </div>
  </div>
);

export const StaticPlannerPanel: React.FC<StaticPlannerPanelProps> = ({
  raceDurationMinutes,
  fuelCapacity,
  avgFuelPerLap,
  avgLapTime,
  pitInTime,
  pitOutTime,
  fuelingTime,
  prefillFuelSavingCalibration,
  onSelectPlan,
  selectedPlanId,
}) => {
  const [selectedClass, setSelectedClass] = useState<CarClass | 'none'>('GTP');
  const [expandedPlanId, setExpandedPlanId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [useFuelSaveCalibration, setUseFuelSaveCalibration] = useState(Boolean(prefillFuelSavingCalibration));
  const [saveLapTime, setSaveLapTime] = useState<number>(prefillFuelSavingCalibration?.saveLapTime ?? 0);
  const [saveFuelPerLap, setSaveFuelPerLap] = useState<number>(prefillFuelSavingCalibration?.saveFuelPerLap ?? 0);
  const [leaderLapTime, setLeaderLapTime] = useState<number>(0);
  const [fuelSafetyMargin, setFuelSafetyMargin] = useState<number>(0.3);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [paceProfiles, setPaceProfiles] = useState<PaceProfile[]>([]);

  const planner = useMemo(() => new StaticStrategyPlanner(), []);

  useEffect(() => {
    if (!prefillFuelSavingCalibration) {
      return;
    }

    setUseFuelSaveCalibration(true);
    setSaveLapTime(prefillFuelSavingCalibration.saveLapTime);
    setSaveFuelPerLap(prefillFuelSavingCalibration.saveFuelPerLap);
  }, [
    prefillFuelSavingCalibration?.saveFuelPerLap,
    prefillFuelSavingCalibration?.saveLapTime,
  ]);

  const defaultMaxFuelSavePercent = useMemo(() => {
    switch (selectedClass) {
      case 'GTP':
        return 15;
      case 'LMP2':
        return 12;
      case 'GT3':
        return 15;
      default:
        return 15;
    }
  }, [selectedClass]);

  const defaultPenaltyPerPercent = useMemo(() => {
    switch (selectedClass) {
      case 'GTP':
        return 0.10;
      case 'LMP2':
        return 0.18;
      case 'GT3':
        return 0.13;
      default:
        return 0.15;
    }
  }, [selectedClass]);

  const fuelSavingCalibration: FuelSavingCalibration | undefined = useMemo(() => {
    if (!useFuelSaveCalibration || saveLapTime <= 0 || saveFuelPerLap <= 0) {
      return undefined;
    }

    return {
      baselineLapTime: avgLapTime,
      baselineFuelPerLap: avgFuelPerLap,
      saveLapTime,
      saveFuelPerLap,
    };
  }, [useFuelSaveCalibration, saveLapTime, saveFuelPerLap, avgLapTime, avgFuelPerLap]);

  const plannerInput: StaticStrategyInput = useMemo(() => ({
    fuelCapacity,
    avgFuelPerLap,
    avgLapTime,
    raceDurationMinutes,
    carClass: selectedClass === 'none' ? undefined : selectedClass,
    pitInTime,
    pitOutTime,
    fuelingTime,
    fuelSavingCalibration,
    leaderLapTime: leaderLapTime > 0 ? leaderLapTime : undefined,
    fuelSafetyMargin,
    paceProfiles: paceProfiles.filter(p => p.lapTime > 0 && p.fuelPerLap > 0),
  }), [fuelCapacity, avgFuelPerLap, avgLapTime, raceDurationMinutes, selectedClass, pitInTime, pitOutTime, fuelingTime, fuelSavingCalibration, leaderLapTime, fuelSafetyMargin, paceProfiles]);

  const calibrationModel = useMemo(() => buildFuelSavingModel({
    calibration: fuelSavingCalibration,
    defaultMaxFuelSavePercent,
    defaultPenaltyPerPercent,
  }), [fuelSavingCalibration, defaultMaxFuelSavePercent, defaultPenaltyPerPercent]);

  const result: StaticStrategyResult | null = useMemo(() => {
    if (!avgLapTime || !avgFuelPerLap || avgFuelPerLap <= 0 || !raceDurationMinutes || !fuelCapacity) {
      return null;
    }
    return planner.planRace(plannerInput);
  }, [planner, plannerInput]);

  if (!result || result.bestPlan.totalLaps === 0) {
    return null;
  }

  const best = result.bestPlan;
  const referenceFullSpeedPlan = result.allPlans.find((plan) => plan.fuelSavePercent === 0);
  const referenceFuelSavePlan = result.allPlans.find((plan) => plan.isCalibrationReference);
  const bestTradeoff = getPlanTradeoff(best, referenceFullSpeedPlan);
  const plans = showAll ? result.allPlans : (() => {
    const visiblePlans: RacePlan[] = [];
    const pushUnique = (plan?: RacePlan) => {
      if (!plan || visiblePlans.some((entry) => entry.id === plan.id)) {
        return;
      }
      visiblePlans.push(plan);
    };

    pushUnique(best);
    pushUnique(referenceFullSpeedPlan);
    pushUnique(referenceFuelSavePlan);

    for (const plan of result.allPlans) {
      pushUnique(plan);
      if (visiblePlans.length >= 8) {
        break;
      }
    }

    return visiblePlans;
  })();

  return (
    <div className="bg-slate-800 p-6 rounded-xl border border-slate-700">
      {/* Header + Class Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl font-semibold text-white">
            Strategy Planner
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Pre-race optimizer — compares all viable race plans
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-400">Car Class:</label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value as CarClass | 'none')}
            className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {CAR_CLASSES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <button
            onClick={() => setShowAdvanced(v => !v)}
            className="text-xs text-slate-400 hover:text-slate-200 px-2 py-1.5 rounded border border-slate-600 hover:border-slate-500 transition-colors"
          >
            {showAdvanced ? 'Hide advanced' : 'Advanced…'}
          </button>
        </div>
      </div>

      {showAdvanced && (
        <div className="mb-4 rounded-lg border border-slate-700 bg-slate-900/40 p-4">
          <h3 className="text-sm font-semibold text-white mb-3">Multi-class &amp; Safety</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <TimePicker
                label="Leader Lap Time (optional)"
                value={leaderLapTime}
                onChange={setLeaderLapTime}
                placeholder="1:50.000"
              />
              <p className="text-xs text-slate-500 mt-1">GTP/LMDh pace — extends effective race window for slower classes.</p>
            </div>
            <div>
              <FuelInput
                label="Fuel Safety Margin (L)"
                value={fuelSafetyMargin}
                onChange={(v) => setFuelSafetyMargin(Math.max(0.05, v))}
                placeholder="0.3"
                min={0.05}
                max={5}
              />
              <p className="text-xs text-slate-500 mt-1">Min fuel in tank before pitting. Engine stutters below 0.3L — lower only if consumption is precisely calibrated.</p>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 rounded-lg border border-slate-700 bg-slate-900/40 p-4">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div>
            <h3 className="text-sm font-semibold text-white">Fuel-Save Calibration</h3>
            <p className="text-xs text-slate-400 mt-1">
              Uses the main avg lap and fuel inputs as full-speed baseline, plus one saved-fuel reference stint.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={useFuelSaveCalibration}
              onChange={(e) => setUseFuelSaveCalibration(e.target.checked)}
              className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500"
            />
            Use reference
          </label>
        </div>

        {useFuelSaveCalibration && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <TimePicker
                label="Fuel-Save Lap Time"
                value={saveLapTime}
                onChange={setSaveLapTime}
                placeholder="02:01.000"
              />
            </div>
            <div>
              <FuelInput
                label="Fuel-Save Consumption (L/lap)"
                value={saveFuelPerLap}
                onChange={setSaveFuelPerLap}
                placeholder="3.10"
                min={0}
                max={20}
              />
            </div>
            <div className="md:col-span-2 text-xs text-slate-500">
              The full-speed inputs define your fastest achievable baseline. The saved-fuel reference defines the time-loss slope, not a hard cap on future saving suggestions.
            </div>
            {fuelSavingCalibration && calibrationModel.calibrationActive && (
              <div className="md:col-span-2 rounded border border-slate-700 bg-slate-950/50 px-3 py-2 text-xs text-slate-300">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <div className="text-slate-500">Observed Save</div>
                    <div className="font-semibold text-white">{calibrationModel.observedSavePercent?.toFixed(2)}%</div>
                  </div>
                  <div>
                    <div className="text-slate-500">Time Loss per 1% Save</div>
                    <div className="font-semibold text-white">{calibrationModel.lapTimePenaltyPerPercent.toFixed(2)}s</div>
                  </div>
                  <div>
                    <div className="text-slate-500">Planner Save Range</div>
                    <div className="font-semibold text-white">0-{calibrationModel.maxFuelSavePercent.toFixed(0)}%</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pace Profiles */}
      <div className="mb-6 rounded-lg border border-slate-700 bg-slate-900/40 p-4">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div>
            <h3 className="text-sm font-semibold text-white">Pace Profiles</h3>
            <p className="text-xs text-slate-400 mt-1">
              Compare different driving modes. Your main inputs are the &quot;Full Speed&quot; baseline.
            </p>
          </div>
          <button
            onClick={() => setPaceProfiles(prev => [...prev, { name: '', lapTime: 0, fuelPerLap: 0 }])}
            className="px-3 py-1.5 text-xs font-semibold text-blue-400 border border-blue-500/40 rounded-lg hover:bg-blue-500/20 transition-colors"
          >
            + Add Profile
          </button>
        </div>
        {paceProfiles.length > 0 && (
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_140px_100px_24px] gap-2 text-xs text-slate-500 px-1">
              <div>Name</div>
              <div>Avg Lap Time</div>
              <div>Fuel/Lap</div>
              <div></div>
            </div>
            {paceProfiles.map((p, i) => (
              <div key={i} className="grid grid-cols-[1fr_140px_100px_24px] gap-2 items-center">
                <input
                  value={p.name}
                  onChange={(e) => setPaceProfiles(prev => prev.map((pp, j) => j === i ? { ...pp, name: e.target.value } : pp))}
                  className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. Light Save"
                />
                <input
                  value={p.lapTime > 0 ? (() => { const t = Math.round(p.lapTime * 1000); const m = Math.floor(t / 60000); const s = Math.floor((t % 60000) / 1000); const ms = t % 1000; return `${m}:${s.toString().padStart(2,'0')}.${ms.toString().padStart(3,'0')}`; })() : ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    let secs = 0;
                    if (v.includes(':')) {
                      const [mm, rest] = v.split(':');
                      secs = (parseInt(mm) || 0) * 60 + (parseFloat(rest) || 0);
                    } else {
                      secs = parseFloat(v) || 0;
                    }
                    setPaceProfiles(prev => prev.map((pp, j) => j === i ? { ...pp, lapTime: secs } : pp));
                  }}
                  className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs font-mono focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="2:01.000"
                />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={p.fuelPerLap > 0 ? p.fuelPerLap : ''}
                  onChange={(e) => setPaceProfiles(prev => prev.map((pp, j) => j === i ? { ...pp, fuelPerLap: parseFloat(e.target.value) || 0 } : pp))}
                  className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs font-mono focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="3.10"
                />
                <button
                  onClick={() => setPaceProfiles(prev => prev.filter((_, j) => j !== i))}
                  className="text-red-400 hover:text-red-300 text-sm leading-none"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stop Count Targets */}
      {result.stopCountTargets.length > 0 && (
        <div className="mb-6 rounded-lg border border-slate-700 bg-slate-900/40 p-4">
          <h3 className="text-sm font-semibold text-white mb-2">Stop Count Targets</h3>
          <p className="text-xs text-slate-400 mb-3">
            What performance is needed for each stop count? Fewer stops save pit time but require lower fuel consumption.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700 text-slate-400 text-xs">
                  <th className="px-3 py-2 text-left">Stops</th>
                  <th className="px-3 py-2 text-right">Max Fuel/Lap</th>
                  <th className="px-3 py-2 text-right">Laps/Stint</th>
                  <th className="px-3 py-2 text-right">Total Pit Time</th>
                  <th className="px-3 py-2 text-left">Pace Budget vs Full Speed</th>
                </tr>
              </thead>
              <tbody>
                {result.stopCountTargets.map(t => (
                  <tr key={t.stops} className={`border-b border-slate-700/50 ${t.isBaseline ? 'bg-green-900/20' : ''}`}>
                    <td className="px-3 py-2 font-mono text-white font-semibold">
                      {t.stops}{t.isBaseline ? ' ★' : ''}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-slate-300">
                      ≤ {t.maxFuelPerLap.toFixed(2)}L
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-slate-300">
                      {t.stintLaps}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-slate-300">
                      {formatTime(t.totalPitTime)}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {t.breakEvenLapTime != null && t.breakEvenDelta != null ? (
                        <span className="text-yellow-400">
                          +{t.breakEvenDelta.toFixed(2)}s/lap → max {formatLapTime(t.breakEvenLapTime)}
                        </span>
                      ) : t.isBaseline ? (
                        <span className="text-green-400">full speed baseline</span>
                      ) : (
                        <span className="text-slate-500">extra stops — slower than baseline</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Profile Comparison */}
      {result.profileResults.length > 0 && (
        <div className="mb-6 rounded-lg border border-blue-500/30 bg-blue-900/10 p-4">
          <h3 className="text-sm font-semibold text-white mb-3">Which Pace Wins?</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700 text-slate-400 text-xs">
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Profile</th>
                  <th className="px-3 py-2 text-right">Lap Time</th>
                  <th className="px-3 py-2 text-right">Fuel/Lap</th>
                  <th className="px-3 py-2 text-right">Stops</th>
                  <th className="px-3 py-2 text-right">Laps</th>
                  <th className="px-3 py-2 text-right">Race Time</th>
                  <th className="px-3 py-2 text-right">vs Best</th>
                </tr>
              </thead>
              <tbody>
                {result.profileResults.map((r, i) => {
                  const isProfileBest = i === 0 && r.bestPlan != null;
                  return (
                    <tr key={i} className={`border-b border-slate-700/50 ${isProfileBest ? 'bg-green-900/20' : ''}`}>
                      <td className="px-3 py-2 font-mono">{isProfileBest ? '★' : i + 1}</td>
                      <td className="px-3 py-2 font-medium text-white">{r.profile.name}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-300">{formatLapTime(r.profile.lapTime)}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-300">{r.profile.fuelPerLap.toFixed(2)}L</td>
                      <td className="px-3 py-2 text-right font-mono">{r.bestPlan?.pitStops ?? '—'}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-white">{r.bestPlan?.totalLaps ?? '—'}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-300">{r.bestPlan ? formatTime(r.bestPlan.totalRaceTime) : '—'}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {isProfileBest ? (
                          <span className="text-green-400 font-bold">BEST</span>
                        ) : r.bestPlan ? (
                          <span className="text-red-400">
                            {r.lapDeltaVsBest > 0 && <span>−{r.lapDeltaVsBest} lap{r.lapDeltaVsBest > 1 ? 's' : ''}</span>}
                            {r.lapDeltaVsBest === 0 && <span>+{r.timeDeltaVsBest.toFixed(1)}s</span>}
                          </span>
                        ) : (
                          <span className="text-slate-500">N/A</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Best Plan Highlight */}
      <div className={`rounded-lg p-4 mb-6 border ${
        selectedPlanId === best.id
          ? 'bg-blue-900/30 border-blue-500/60'
          : 'bg-green-900/30 border-green-500/40'
      }`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-green-400 text-lg font-bold">★ Best Strategy</span>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${CATEGORY_COLORS[best.category] || 'bg-slate-700 text-slate-300'}`}>
              {CATEGORY_LABELS[best.category] || best.category}
            </span>
            {selectedPlanId === best.id && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/30 text-blue-300 border border-blue-500/40">● Active</span>
            )}
          </div>
          {onSelectPlan && (
            <button
              onClick={(e) => { e.stopPropagation(); onSelectPlan(best, plannerInput); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                selectedPlanId === best.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/40 border border-blue-500/40'
              }`}
            >
              {selectedPlanId === best.id ? '✓ Selected' : 'Use for Live'}
            </button>
          )}
        </div>
        <div className="text-white font-semibold text-lg mb-2">{best.name}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-slate-400">Total Laps</div>
            <div className="text-xl font-bold text-white">{best.totalLaps}</div>
          </div>
          <div>
            <div className="text-slate-400">Pit Stops</div>
            <div className="text-xl font-bold text-white">{best.pitStops}</div>
          </div>
          <div>
            <div className="text-slate-400">Race Time</div>
            <div className="text-xl font-bold text-white">{formatTime(best.totalRaceTime)}</div>
          </div>
          <div>
            <div className="text-slate-400">Fuel Save</div>
            <div className="text-xl font-bold text-white">
              {best.fuelSavePercent > 0 ? formatSavePercent(best.fuelSavePercent) : 'None'}
            </div>
          </div>
        </div>
        {bestTradeoff && referenceFullSpeedPlan && (
          <StrategyTradeoffCard tradeoff={bestTradeoff} baselineName={referenceFullSpeedPlan.name} />
        )}
        {/* Best Plan Stint Breakdown */}
        <StintBreakdown stints={best.stints} fuelCapacity={fuelCapacity} />
      </div>

      {/* All Plans Comparison Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-700 text-slate-400">
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-left">Strategy</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-right">Laps</th>
              <th className="px-3 py-2 text-right" title="Race lap range where this stop count is optimal">Valid Laps</th>
              <th className="px-3 py-2 text-right">Stops</th>
              <th className="px-3 py-2 text-right">Save %</th>
              <th className="px-3 py-2 text-right">Lap Time</th>
              <th className="px-3 py-2 text-right">Fuel/Lap</th>
              <th className="px-3 py-2 text-right">Race Time</th>
              <th className="px-3 py-2 text-right">vs Best</th>
              {onSelectPlan && <th className="px-3 py-2 text-center">Live</th>}
            </tr>
          </thead>
          <tbody>
            {plans.map((plan, i) => {
              const isBest = plan.id === best.id;
              const isExpanded = expandedPlanId === plan.id;
              const isSelected = selectedPlanId === plan.id;
              const tradeoff = getPlanTradeoff(plan, referenceFullSpeedPlan);
              const feasibility = getStintFeasibility(plan, plannerInput);
              return (
                <React.Fragment key={plan.id}>
                  <tr
                    className={`
                      border-b border-slate-700/50 cursor-pointer transition-colors
                      ${isSelected ? 'bg-blue-900/20 border-l-2 border-l-blue-500' : isBest ? 'bg-green-900/20' : 'hover:bg-slate-700/30'}
                    `}
                    onClick={() => setExpandedPlanId(isExpanded ? null : plan.id)}
                  >
                    <td className="px-3 py-2 font-mono">
                      {isBest ? '★' : i + 1}
                    </td>
                    <td className="px-3 py-2 font-medium text-white max-w-[200px] truncate" title={plan.name}>
                      {plan.name}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2 justify-start">
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${CATEGORY_COLORS[plan.category] || 'bg-slate-700 text-slate-300'}`}>
                          {CATEGORY_LABELS[plan.category] || plan.category}
                        </span>
                        {plan.isCalibrationReference && (
                          <span className="text-xs px-2 py-0.5 rounded-full border bg-blue-500/20 text-blue-300 border-blue-500/40">
                            Reference
                          </span>
                        )}
                        {plan.fuelSavePercent === 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full border bg-slate-700/70 text-slate-200 border-slate-500/40">
                            Baseline
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-white">{plan.totalLaps}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-400 text-xs">
                      {plan.lapRangeLow != null || plan.lapRangeHigh != null ? (
                        <span className={
                          plan.lapRangeLow != null && plan.lapRangeHigh != null &&
                          best.totalLaps >= plan.lapRangeLow && best.totalLaps <= plan.lapRangeHigh
                            ? 'text-green-400'
                            : ''
                        }>
                          {plan.lapRangeLow ?? '—'}–{plan.lapRangeHigh ?? '—'}
                        </span>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{plan.pitStops}</td>
                    <td className="px-3 py-2 text-right font-mono">
                      {plan.fuelSavePercent > 0 ? (
                        <span className="text-yellow-400">{formatSavePercent(plan.fuelSavePercent)}</span>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-slate-300">{formatLapTime(plan.adjustedLapTime)}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-300">{plan.adjustedFuelPerLap.toFixed(2)}L
                      {feasibility && (
                        <div
                          className={`text-xs mt-0.5 ${
                            feasibility.ok
                              ? 'text-green-400'
                              : feasibility.delta <= 0.05
                              ? 'text-yellow-400'
                              : 'text-red-400'
                          }`}
                          title={`${plan.maxStintLaps}-lap stints require \u2264${feasibility.maxFuelRate.toFixed(3)} L/lap`}
                        >
                          {feasibility.ok
                            ? `\u2713 \u2264${feasibility.maxFuelRate.toFixed(2)}L`
                            : `\u26a0 +${feasibility.delta.toFixed(3)}L over`}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-slate-300">{formatTime(plan.totalRaceTime)}</td>
                    <td className="px-3 py-2 text-right font-mono">
                      {isBest ? (
                        <span className="text-green-400 font-bold">BEST</span>
                      ) : (
                        <span className="text-red-400">
                          {plan.lapDeltaVsBest > 0 && `-${plan.lapDeltaVsBest}L `}
                          +{plan.timeDeltaVsBest.toFixed(1)}s
                        </span>
                      )}
                    </td>
                    {onSelectPlan && (
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={(e) => { e.stopPropagation(); onSelectPlan(plan, plannerInput); }}
                          className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${
                            isSelected
                              ? 'bg-blue-600 text-white'
                              : 'bg-slate-700 text-slate-400 hover:bg-blue-500/30 hover:text-blue-300'
                          }`}
                        >
                          {isSelected ? '\u2713' : 'Use'}
                        </button>
                      </td>
                    )}
                  </tr>
                  {/* Expanded Stint Row */}
                  {isExpanded && (
                    <tr>
                      <td colSpan={onSelectPlan ? 11 : 10} className="px-3 py-2 bg-slate-900/50">
                        {tradeoff && referenceFullSpeedPlan && (
                          <StrategyTradeoffCard tradeoff={tradeoff} baselineName={referenceFullSpeedPlan.name} />
                        )}
                        <StintBreakdown stints={plan.stints} fuelCapacity={fuelCapacity} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Show More / Less */}
      {result.allPlans.length > 8 && (
        <div className="text-center mt-4">
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
          >
            {showAll ? `Show Top 8 ▲` : `Show All ${result.allPlans.length} Plans ▼`}
          </button>
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 pt-4 border-t border-slate-700 flex flex-wrap gap-3 text-xs">
        {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
          <span key={key} className={`px-2 py-0.5 rounded-full border ${CATEGORY_COLORS[key]}`}>
            {label}
          </span>
        ))}
        <span className="text-slate-500 ml-2">Click a row to see stint details</span>
      </div>
    </div>
  );
};

/**
 * Stint breakdown sub-component — shown for expanded plans.
 */
const StintBreakdown: React.FC<{ stints: PlanStint[]; fuelCapacity: number }> = ({ stints, fuelCapacity }) => {
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-slate-500 border-b border-slate-700/50">
            <th className="px-2 py-1 text-left">Stint</th>
            <th className="px-2 py-1 text-right">Laps</th>
            <th className="px-2 py-1 text-right">Fuel Start</th>
            <th className="px-2 py-1 text-right">Fuel End</th>
            <th className="px-2 py-1 text-right">Stint Time</th>
            <th className="px-2 py-1 text-center">Pit</th>
            <th className="px-2 py-1 text-right">Fuel Added</th>
            <th className="px-2 py-1 text-right">Pit Time</th>
          </tr>
        </thead>
        <tbody>
          {stints.map((stint) => (
            <tr key={stint.stintNumber} className="border-b border-slate-800/50">
              <td className="px-2 py-1 font-mono text-slate-300">S{stint.stintNumber}</td>
              <td className="px-2 py-1 text-right font-mono text-white">{stint.laps}</td>
              <td className="px-2 py-1 text-right font-mono text-slate-300">{stint.fuelAtStart.toFixed(1)}L</td>
              <td className="px-2 py-1 text-right font-mono text-slate-300">{stint.fuelAtEnd.toFixed(1)}L</td>
              <td className="px-2 py-1 text-right font-mono text-slate-300">{formatTime(stint.stintTime)}</td>
              <td className="px-2 py-1 text-center">
                {stint.pitAfter ? (
                  (() => {
                    const isTrulyFull = stint.pitType === 'full' && stint.fuelAdded >= fuelCapacity * 0.95;
                    const label = stint.pitType === 'splash' ? 'Splash'
                      : isTrulyFull ? 'Full' : 'Partial';
                    const cls = stint.pitType === 'splash'
                      ? 'bg-orange-500/20 text-orange-400'
                      : isTrulyFull
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-blue-500/20 text-blue-400';
                    return <span className={`px-1.5 py-0.5 rounded text-xs ${cls}`}>{label}</span>;
                  })()
                ) : (
                  <span className="text-slate-600">—</span>
                )}
              </td>
              <td className="px-2 py-1 text-right font-mono text-slate-300">
                {stint.fuelAdded > 0 ? `+${stint.fuelAdded.toFixed(1)}L` : '—'}
              </td>
              <td className="px-2 py-1 text-right font-mono text-slate-300">
                {stint.pitTime > 0 ? `${stint.pitTime.toFixed(0)}s` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
